import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import {
  PhoneCall,
  PhoneOff,
  Mic,
  MicOff,
  MessageSquare,
  Building2,
  MapPin,
  Mail,
  Phone,
  Clock,
  Sparkles,
  ShieldCheck,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Volume2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { webContactLinksApi } from "@/api/webContactLinks/webContactLinksApi";
import { useWebVisitorCall } from "@/hooks/useWebVisitorCall";
import { CallPopup } from "@/calling/components/CallPopup.jsx";
import { CALL_STATES } from "@/calling/callConstants.js";
import { departmentDisplayName, HOTEL_DEPARTMENTS } from "@/utils/alerts/departmentAlerts.js";

export default function PublicWebConnectPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialDeptParam = searchParams.get("dept") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const [selectedDept, setSelectedDept] = useState("Reception");
  const [visitorName, setVisitorName] = useState(() => localStorage.getItem("visitor_name") || "");

  // Message Form State
  const [inquiryForm, setInquiryForm] = useState({
    visitorName: localStorage.getItem("visitor_name") || "",
    visitorPhone: localStorage.getItem("visitor_phone") || "",
    visitorEmail: localStorage.getItem("visitor_email") || "",
    subject: "",
    message: "",
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquirySent, setInquirySent] = useState(null);

  const remoteAudioRef = useRef(null);

  // Load Link Metadata
  useEffect(() => {
    if (!token) {
      setError("No contact link token provided.");
      setLoading(false);
      return;
    }

    const fetchMeta = async () => {
      try {
        setLoading(true);
        const res = await webContactLinksApi.getPublicMetadata(token);
        const metaData = res.data?.data || res.data?.Data || res.data;
        setMetadata(metaData);
        const defaultDept = metaData?.defaultDepartment || "Reception";
        setSelectedDept(initialDeptParam || defaultDept);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "This contact link is invalid, expired, or has been revoked by the hotel."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMeta();
  }, [token, initialDeptParam]);

  // Voice Calling Engine
  const callEngine = useWebVisitorCall({
    token,
    visitorName,
    metadata,
    remoteAudioRef,
  });

  const handleStartCall = async (dept) => {
    const targetDept = dept || selectedDept || metadata?.defaultDepartment || "Reception";
    if (visitorName.trim()) {
      localStorage.setItem("visitor_name", visitorName.trim());
    }
    try {
      await callEngine.callDepartment(targetDept);
      // Record call placed count
      webContactLinksApi.recordCallPlaced(token).catch(() => {});
    } catch (err) {
      toast.error(err?.message || "Failed to start call. Please try again.");
    }
  };

  const handleSendInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryForm.message.trim()) {
      toast.error("Please enter your message or question.");
      return;
    }

    try {
      setSubmittingInquiry(true);
      if (inquiryForm.visitorName) localStorage.setItem("visitor_name", inquiryForm.visitorName);
      if (inquiryForm.visitorPhone) localStorage.setItem("visitor_phone", inquiryForm.visitorPhone);
      if (inquiryForm.visitorEmail) localStorage.setItem("visitor_email", inquiryForm.visitorEmail);

      const res = await webContactLinksApi.submitInquiry(token, {
        visitorName: inquiryForm.visitorName,
        visitorPhone: inquiryForm.visitorPhone,
        visitorEmail: inquiryForm.visitorEmail,
        department: selectedDept,
        subject: inquiryForm.subject,
        message: inquiryForm.message,
      });

      setInquirySent(res.data);
      toast.success("Inquiry sent directly to front desk!");
      setInquiryForm((prev) => ({ ...prev, subject: "", message: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit inquiry.");
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const allowedDepts = useMemo(() => {
    if (metadata?.allowedDepartments?.length > 0) {
      return metadata.allowedDepartments;
    }
    return ["Reception", "Reservations", "Concierge"];
  }, [metadata]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-white">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-400 mb-4" />
        <p className="text-sm font-medium text-slate-300">Connecting to hotel concierge...</p>
      </div>
    );
  }

  if (error || !metadata) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-500/20 bg-slate-900/90 text-white p-6 text-center backdrop-blur-xl shadow-2xl">
          <AlertCircle className="h-12 w-12 text-rose-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-100">Link Unavailable</h2>
          <p className="mt-2 text-sm text-slate-400">{error || "This contact channel is not found."}</p>
          <div className="mt-6 pt-4 border-t border-slate-800 flex justify-center">
            <Link to="/" className="text-xs text-emerald-400 hover:underline">
              Return to Homepage
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const isCallActive =
    callEngine.callState === CALL_STATES.calling ||
    callEngine.callState === CALL_STATES.connecting ||
    callEngine.callState === CALL_STATES.connected;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex flex-col justify-between selection:bg-emerald-500 selection:text-white">
      <Helmet>
        <title>{`${metadata.hotelName} | Direct Concierge & Voice Call`}</title>
      </Helmet>

      {/* Hidden Audio Element for LiveKit Audio */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {/* Floating In-Call Popup */}
      <CallPopup
        callState={callEngine.callState}
        callInfo={callEngine.callInfo}
        activeCall={callEngine.activeCall}
        incomingCall={callEngine.incomingCall}
        muted={callEngine.muted}
        isHeld={callEngine.isHeld}
        isRemoteHeld={callEngine.isRemoteHeld}
        isTransferring={callEngine.isTransferring}
        transferringDepartment={callEngine.transferringDepartment}
        callDuration={callEngine.callDuration}
        onAccept={callEngine.acceptCall}
        onDecline={callEngine.declineCall}
        onEnd={callEngine.endCall}
        onToggleMute={callEngine.toggleMute}
      />

      {/* Header Banner */}
      <header className="border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-30 px-4 py-3 sm:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {metadata.hotelLogoUrl ? (
              <img
                src={metadata.hotelLogoUrl}
                alt={metadata.hotelName}
                className="h-10 w-10 rounded-xl object-cover border border-slate-700 bg-white/5"
              />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold">
                <Building2 className="h-5 w-5" />
              </div>
            )}
            <div>
              <h1 className="text-base font-bold text-white tracking-tight leading-none">
                {metadata.hotelName}
              </h1>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live Desk Available • {metadata.title}
              </p>
            </div>
          </div>

          <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs hidden sm:flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Direct Operator Link
          </Badge>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto w-full px-4 py-8 sm:py-12 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          {/* Left Column: Interactive Call & Message Hub */}
          <div className="md:col-span-7 space-y-6">
            <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-2xl overflow-hidden rounded-2xl">
              <CardHeader className="bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-900 border-b border-slate-800 pb-5">
                <div className="flex items-center justify-between">
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs font-semibold">
                    Instant Voice Call
                  </Badge>
                  <span className="text-xs text-slate-400 font-mono">No App Required</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-white mt-2">
                  Speak Directly to Reception
                </CardTitle>
                <CardDescription className="text-slate-300 text-xs sm:text-sm">
                  Click the button below to initiate high-definition voice audio directly to the on-duty hotel staff desk.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 space-y-6">
                {/* Visitor Identification (Optional) */}
                <div className="space-y-1.5">
                  <Label htmlFor="visitor-name" className="text-xs font-medium text-slate-300">
                    Your Name (Optional - helps reception address you)
                  </Label>
                  <Input
                    id="visitor-name"
                    placeholder="e.g. John Doe"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                    className="bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 text-sm h-10"
                    disabled={isCallActive}
                  />
                </div>

                {/* Department Selection */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-slate-300">
                    Select Department to Connect
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {allowedDepts.map((dept) => {
                      const selected = selectedDept === dept;
                      return (
                        <button
                          key={dept}
                          type="button"
                          disabled={isCallActive}
                          onClick={() => setSelectedDept(dept)}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            selected
                              ? "bg-emerald-500/20 border-emerald-500/60 text-white shadow-lg shadow-emerald-950/50"
                              : "bg-slate-950/40 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
                          } ${isCallActive ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <div className="text-xs font-semibold">{departmentDisplayName(dept)}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">Online Desk</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Call Action Display */}
                <div className="rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 border border-slate-800 p-6 flex flex-col items-center justify-center text-center relative overflow-hidden">
                  {callEngine.callState === CALL_STATES.queued ? (
                    <div className="space-y-4 w-full animate-in fade-in duration-300">
                      <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
                        <div className="h-16 w-16 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center text-amber-300 shadow-xl shadow-amber-500/20">
                          <Clock className="h-8 w-8 animate-pulse" />
                        </div>
                      </div>

                      <div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-bold mb-2">
                          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                          Line #{callEngine.callInfo?.queuePosition || 1} in Waiting Queue
                        </div>
                        <h4 className="text-base font-bold text-white">
                          Holding for {departmentDisplayName(selectedDept)}
                        </h4>
                        <p className="text-xs text-slate-300 mt-1 max-w-sm mx-auto leading-relaxed">
                          All front desk staff are currently occupied on other lines. Please stay on the line — your call will automatically connect as soon as an agent is free.
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                        <Button
                          variant="destructive"
                          className="rounded-full px-6 h-11 gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold cursor-pointer"
                          onClick={callEngine.endCall}
                        >
                          <PhoneOff className="h-5 w-5" /> Cancel Call
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full px-5 h-11 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white text-xs cursor-pointer"
                          onClick={() => {
                            callEngine.endCall();
                            document.getElementById("inquiry-section")?.scrollIntoView({ behavior: "smooth" });
                          }}
                        >
                          <MessageSquare className="h-4 w-4 mr-1.5 text-emerald-400" /> Leave a Message Instead
                        </Button>
                      </div>
                    </div>
                  ) : isCallActive ? (
                    <div className="space-y-4 w-full">
                      <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
                        <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
                        <div className="h-16 w-16 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-xl shadow-emerald-500/30">
                          <Volume2 className="h-8 w-8 animate-pulse" />
                        </div>
                      </div>

                      <div>
                        <h4 className="text-base font-bold text-white">
                          {callEngine.callState === CALL_STATES.connected
                            ? "Connected to Hotel Desk"
                            : `Calling ${departmentDisplayName(selectedDept)}...`}
                        </h4>
                        <p className="text-xs text-slate-400 mt-1">
                          {callEngine.callState === CALL_STATES.connected
                            ? "High-Definition Audio Active"
                            : "Waiting for operator to pick up..."}
                        </p>
                      </div>

                      <div className="flex items-center justify-center gap-3 pt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-11 w-11 rounded-full border-slate-700 bg-slate-800 text-white hover:bg-slate-700 cursor-pointer"
                          onClick={callEngine.toggleMute}
                        >
                          {callEngine.muted ? <MicOff className="h-5 w-5 text-rose-400" /> : <Mic className="h-5 w-5" />}
                        </Button>

                        <Button
                          variant="destructive"
                          className="rounded-full px-6 h-11 gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold cursor-pointer"
                          onClick={callEngine.endCall}
                        >
                          <PhoneOff className="h-5 w-5" /> End Call
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 w-full">
                      <button
                        type="button"
                        onClick={() => handleStartCall(selectedDept)}
                        className="group relative mx-auto w-24 h-24 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-white shadow-2xl shadow-emerald-500/40 hover:scale-105 active:scale-95 transition-all duration-300 cursor-pointer"
                      >
                        <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-pulse group-hover:scale-110 transition-transform" />
                        <PhoneCall className="h-10 w-10 relative z-10 group-hover:rotate-12 transition-transform" />
                      </button>

                      <div>
                        <h4 className="text-base font-bold text-white">
                          Call {departmentDisplayName(selectedDept)}
                        </h4>
                        <p className="text-xs text-slate-400 mt-1">
                          Free browser voice call • Connects in seconds
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Message / Inquiries Form */}
            <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-xl rounded-2xl">
              <CardHeader className="pb-3 border-b border-slate-800">
                <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-400" /> Send Instant Inquiry
                </CardTitle>
                <CardDescription className="text-slate-300 text-xs">
                  Prefer messaging? Send your booking questions or requests directly to hotel staff.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6">
                {inquirySent ? (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center space-y-3">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                    <div>
                      <h4 className="text-sm font-bold text-white">Inquiry Received</h4>
                      <p className="text-xs text-slate-300 mt-1">
                        Your message has been assigned Ticket <strong>#{inquirySent.ticketNumber}</strong>. The hotel team will respond shortly.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-700 text-xs"
                      onClick={() => setInquirySent(null)}
                    >
                      Send Another Inquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSendInquiry} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="inq-phone" className="text-xs text-slate-300">
                          Phone / WhatsApp
                        </Label>
                        <Input
                          id="inq-phone"
                          placeholder="+977 9800000000"
                          value={inquiryForm.visitorPhone}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, visitorPhone: e.target.value })}
                          className="bg-slate-950/60 border-slate-700 text-white text-xs h-9"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="inq-email" className="text-xs text-slate-300">
                          Email Address
                        </Label>
                        <Input
                          id="inq-email"
                          type="email"
                          placeholder="you@example.com"
                          value={inquiryForm.visitorEmail}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, visitorEmail: e.target.value })}
                          className="bg-slate-950/60 border-slate-700 text-white text-xs h-9"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="inq-subject" className="text-xs text-slate-300">
                        Subject / Topic
                      </Label>
                      <Input
                        id="inq-subject"
                        placeholder="e.g. Room availability for this weekend"
                        value={inquiryForm.subject}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, subject: e.target.value })}
                        className="bg-slate-950/60 border-slate-700 text-white text-xs h-9"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="inq-msg" className="text-xs text-slate-300">
                        Your Message <span className="text-rose-400">*</span>
                      </Label>
                      <Textarea
                        id="inq-msg"
                        placeholder="Write your request, dates of travel, or inquiries here..."
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                        className="bg-slate-950/60 border-slate-700 text-white text-xs"
                        rows={3}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submittingInquiry}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 gap-2 shadow-lg shadow-blue-950/50"
                    >
                      {submittingInquiry ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" /> Submit Priority Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Hotel Details & Amenities */}
          <div className="md:col-span-5 space-y-6">
            <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-xl rounded-2xl overflow-hidden">
              {metadata.hotelCoverUrl ? (
                <div className="h-36 w-full overflow-hidden relative">
                  <img
                    src={metadata.hotelCoverUrl}
                    alt={metadata.hotelName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                </div>
              ) : null}

              <CardContent className="p-6 space-y-5">
                <div>
                  <h3 className="text-lg font-bold text-white">{metadata.hotelName}</h3>
                  {metadata.description ? (
                    <p className="text-xs text-slate-300 mt-2 line-clamp-3 leading-relaxed">
                      {metadata.description}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3 pt-2 border-t border-slate-800 text-xs text-slate-300">
                  {metadata.hotelAddress ? (
                    <div className="flex items-start gap-2.5">
                      <MapPin className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{metadata.hotelAddress}</span>
                    </div>
                  ) : null}

                  {metadata.hotelPhone ? (
                    <div className="flex items-center gap-2.5">
                      <Phone className="h-4 w-4 text-emerald-400 shrink-0" />
                      <a href={`tel:${metadata.hotelPhone}`} className="hover:text-white transition-colors underline">
                        {metadata.hotelPhone}
                      </a>
                    </div>
                  ) : null}

                  {metadata.hotelEmail ? (
                    <div className="flex items-center gap-2.5">
                      <Mail className="h-4 w-4 text-emerald-400 shrink-0" />
                      <a href={`mailto:${metadata.hotelEmail}`} className="hover:text-white transition-colors underline">
                        {metadata.hotelEmail}
                      </a>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                    <ShieldCheck className="h-4 w-4" /> Verified Direct Channel
                  </div>
                  <p className="text-slate-400 leading-relaxed">
                    Calls and inquiries from this link are prioritized directly in the hotel's on-site property management system.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 bg-slate-950/80 py-4 px-4 text-center text-xs text-slate-500">
        <p>© {new Date().getFullYear()} {metadata.hotelName}. Powered by Hotel In Himalayas Smart Concierge.</p>
      </footer>
    </div>
  );
}
